import { useMemo, useState } from "react";
import ColumnFilterMenu from "@/components/ColumnFilterMenu";
import { euro } from "@/lib/api";
import { hierTotal } from "@/lib/hierTotal";
import { ArrowUp, ArrowDown } from "lucide-react";

const COLS = [
  { key: "cliente", label: "Cliente", type: "text", align: "left" },
  { key: "nome", label: "Commessa", type: "text", align: "left" },
  { key: "totale", label: "Contabilità", type: "num", align: "right" },
  { key: "subappalto", label: "Subappalto", type: "num", align: "right" },
];
const raw = (r, c) => c.type === "num" ? String(Math.round((r[c.key] || 0) * 100) / 100) : (r[c.key] || "").toString();
const disp = (v, c) => c.type === "num" ? euro(parseFloat(v)) : (v || "—");

export default function CommessaKpiTable({ rows: data }) {
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [filters, setFilters] = useState({});
  const distinct = useMemo(() => {
    const d = {};
    COLS.forEach((c) => { d[c.key] = Array.from(new Set(data.map((r) => raw(r, c)))).sort((a, b) => c.type === "num" ? parseFloat(a) - parseFloat(b) : String(a).localeCompare(String(b), "it", { numeric: true })); });
    return d;
  }, [data]);
  const rows = useMemo(() => {
    let r = data.filter((row) => COLS.every((c) => { const s = filters[c.key]; return !s || s.has(raw(row, c)); }));
    // Raggruppa le sottocommesse sotto la madre, poi ordina gerarchicamente (madri tra loro, figli dentro la madre)
    const groups = [];
    let cur = null;
    r.forEach((row) => {
      if (!row.is_sub) { cur = { parent: row, children: [] }; groups.push(cur); }
      else if (cur) cur.children.push(row);
      else groups.push({ parent: null, children: [row] });
    });
    if (sort.key) {
      const col = COLS.find((c) => c.key === sort.key); const mul = sort.dir === "asc" ? 1 : -1;
      const cmp = (a, b) => col.type === "num"
        ? ((a[sort.key] || 0) - (b[sort.key] || 0)) * mul
        : String(a[sort.key] || "").localeCompare(String(b[sort.key] || ""), "it", { numeric: true }) * mul;
      groups.forEach((g) => g.children.sort(cmp));
      groups.sort((ga, gb) => cmp(ga.parent || ga.children[0] || {}, gb.parent || gb.children[0] || {}));
    }
    const out = [];
    groups.forEach((g) => { if (g.parent) out.push(g.parent); g.children.forEach((c) => out.push(c)); });
    return out;
  }, [data, filters, sort]);
  const toggle = (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
  const tc = hierTotal(data, rows, (x) => x.totale);
  const ts = hierTotal(data, rows, (x) => x.subappalto);

  if (data.length === 0) return <p className="text-sm text-slate-400 py-8 text-center">Nessuna commessa con contabilità registrata.</p>;
  return (
    <div className="overflow-auto flex-1 min-h-0 border border-slate-200 rounded-md">
      <table className="w-full border-collapse text-sm" data-testid="commessa-kpi-table">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-100">
            {COLS.map((c) => (
              <th key={c.key} data-testid={`pc-th-${c.key}`}
                className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 whitespace-nowrap border-b border-slate-200 ${c.align === "right" ? "text-right" : "text-left"}`}>
                <span className={`inline-flex items-center gap-0.5 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                  <button onClick={() => toggle(c.key)} className="inline-flex items-center gap-1 hover:text-slate-800">
                    {c.label}{sort.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                  <ColumnFilterMenu testid={`pc-${c.key}`} values={distinct[c.key]} selected={filters[c.key]} format={(v) => disp(v, c)} onChange={(s) => setFilters((f) => ({ ...f, [c.key]: s }))} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={c.id || c.nome} data-testid={`pc-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2 text-slate-600">{c.cliente || "—"}</td>
              <td className={`px-3 py-2 ${c.is_sub ? "text-slate-500 pl-6" : "text-slate-700 font-medium"}`}>{c.nome}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-800">{euro(c.totale)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-700">{euro(c.subappalto)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="sticky bottom-0">
          <tr className="bg-slate-100 font-semibold border-t border-slate-200">
            <td className="px-3 py-2 text-slate-700 uppercase text-[10px] tracking-widest" colSpan={2}>Totale</td>
            <td className="px-3 py-2 text-right tabular-nums text-blue-800">{euro(tc)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-amber-700">{euro(ts)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
